/**
 * Voice Permission Manager
 * Handles microphone permissions and browser compatibility
 * 
 * @author Software Engineering Team
 * @version 1.0.0
 */

class VoicePermissionManager {
  constructor() {
    this.permissionStatus = null;
    this.supportedBrowsers = ['Chrome', 'Edge', 'Safari', 'Firefox'];
  }

  /**
   * Check if speech recognition is supported in current browser
   * @returns {boolean} - Support status
   */
  isSpeechRecognitionSupported() {
    try {
      const hasWebkitSpeechRecognition = 'webkitSpeechRecognition' in window;
      const hasSpeechRecognition = 'SpeechRecognition' in window;
      
      return hasWebkitSpeechRecognition || hasSpeechRecognition;
    } catch (error) {
      console.error('Error checking speech recognition support:', error);
      return false;
    }
  }

  /**
   * Get detailed browser compatibility information
   * @returns {Object} - Compatibility details
   */
  getBrowserCompatibility() {
    const userAgent = navigator.userAgent;
    const isChrome = /Chrome/.test(userAgent) && /Google Inc/.test(navigator.vendor);
    const isEdge = /Edg/.test(userAgent);
    const isSafari = /Safari/.test(userAgent) && /Apple Computer/.test(navigator.vendor);
    const isFirefox = /Firefox/.test(userAgent);
    
    let browserName = 'Unknown';
    let supportLevel = 'none';
    
    if (isChrome) {
      browserName = 'Chrome';
      supportLevel = 'excellent';
    } else if (isEdge) {
      browserName = 'Edge';
      supportLevel = 'excellent';
    } else if (isSafari) {
      browserName = 'Safari';
      supportLevel = 'good';
    } else if (isFirefox) {
      browserName = 'Firefox';
      supportLevel = 'limited';
    }

    return {
      browserName,
      supportLevel,
      isSupported: this.isSpeechRecognitionSupported(),
      userAgent: userAgent.substring(0, 100) // Truncated for privacy
    };
  }

  /**
   * Check if running on HTTPS (required for microphone access)
   * @returns {boolean} - HTTPS status
   */
  isHTTPS() {
    return window.location.protocol === 'https:' || window.location.hostname === 'localhost';
  }

  /**
   * Check current microphone permission status
   * @returns {Promise<string>} - Permission status
   */
  async checkMicrophonePermission() {
    try {
      if (!navigator.permissions) {
        return 'unknown';
      }

      const permission = await navigator.permissions.query({ name: 'microphone' });
      this.permissionStatus = permission.state;
      
      console.info(`Microphone permission status: ${permission.state}`);
      return permission.state; // 'granted', 'denied', or 'prompt'
      
    } catch (error) {
      console.warn('Could not check microphone permission:', error);
      return 'unknown';
    }
  }

  /**
   * Request microphone permission
   * @returns {Promise<boolean>} - Permission granted status
   */
  async requestMicrophonePermission() {
    try {
      // First check if HTTPS is required
      if (!this.isHTTPS()) {
        throw new Error('HTTPS is required for microphone access');
      }

      // Check current permission status
      const currentStatus = await this.checkMicrophonePermission();
      
      if (currentStatus === 'granted') {
        return true;
      }
      
      if (currentStatus === 'denied') {
        throw new Error('Microphone permission was previously denied. Please enable it in browser settings.');
      }

      // Request permission by attempting to access microphone
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: true,
        video: false 
      });
      
      // Permission granted - clean up the stream
      stream.getTracks().forEach(track => track.stop());
      
      console.info('Microphone permission granted');
      return true;
      
    } catch (error) {
      console.error('Microphone permission request failed:', error);
      
      // Parse specific error types
      if (error.name === 'NotAllowedError') {
        throw new Error('Microphone permission denied by user');
      } else if (error.name === 'NotFoundError') {
        throw new Error('No microphone found on this device');
      } else if (error.name === 'NotSupportedError') {
        throw new Error('Microphone access not supported in this browser');
      } else if (error.name === 'SecurityError') {
        throw new Error('Microphone access blocked due to security restrictions');
      }
      
      throw error;
    }
  }

  /**
   * Get user-friendly error message for permission issues
   * @param {Error} error - Permission error
   * @returns {string} - User-friendly message
   */
  getPermissionErrorMessage(error) {
    const message = error.message.toLowerCase();
    
    if (message.includes('denied')) {
      return 'Microphone access was denied. Please click the microphone icon in your browser\'s address bar to allow access.';
    }
    
    if (message.includes('not found')) {
      return 'No microphone was detected. Please check that your microphone is connected and working.';
    }
    
    if (message.includes('https')) {
      return 'Voice features require a secure connection (HTTPS). Please use HTTPS to enable voice input.';
    }
    
    if (message.includes('not supported')) {
      return 'Voice input is not supported in your current browser. Please try using Chrome, Edge, or Safari.';
    }
    
    return 'Could not access microphone. Please check your browser settings and try again.';
  }

  /**
   * Get recommendations for better voice experience
   * @returns {Array<string>} - Recommendations
   */
  getRecommendations() {
    const compatibility = this.getBrowserCompatibility();
    const recommendations = [];
    
    if (compatibility.supportLevel === 'limited') {
      recommendations.push('For better voice recognition, consider using Chrome or Edge browser');
    }
    
    if (!this.isHTTPS()) {
      recommendations.push('Use HTTPS for secure microphone access');
    }
    
    recommendations.push('Speak clearly and ensure you\'re in a quiet environment');
    recommendations.push('Make sure your microphone is working and not muted');
    
    return recommendations;
  }

  /**
   * Test microphone functionality
   * @returns {Promise<Object>} - Test results
   */
  async testMicrophone() {
    const testResults = {
      isSupported: false,
      hasPermission: false,
      canRecord: false,
      error: null
    };
    
    try {
      // Check support
      testResults.isSupported = this.isSpeechRecognitionSupported();
      
      if (!testResults.isSupported) {
        testResults.error = 'Speech recognition not supported';
        return testResults;
      }
      
      // Check permission
      testResults.hasPermission = await this.requestMicrophonePermission();
      
      if (!testResults.hasPermission) {
        testResults.error = 'Microphone permission denied';
        return testResults;
      }
      
      // Test basic recording capability
      testResults.canRecord = true;
      
    } catch (error) {
      testResults.error = error.message;
    }
    
    return testResults;
  }
}

export default VoicePermissionManager;
